import { useEffect, useMemo, useState } from "react";
import {
  Typography,
  Card,
  Grid,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Stack,
  Button,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/store";
import { fetchEntries } from "@/store/entriesSlice";
import { fetchCategories } from "@/store/categoriesSlice";
import { useExpenseSummary } from "@hooks/useExpenseSummary";
import { useReportsAggregates } from "@hooks/useReportsAggregates";
import { REPORT_CHART_COLORS, BRAND, STAT_ACCENTS } from "@/constants/brand";
import { PageContainer } from "@components/PageContainer";
import { PageHeader } from "@components/PageHeader";
import { StatCard } from "@components/StatCard";
import type { ReportPeriod } from "@/utils/reportPeriod";
import {
  filterEntriesByYmdRange,
  getReportPeriodBounds,
} from "@/utils/reportPeriod";

function PieBlock({
  title,
  data,
  emptyLabel,
}: {
  title: string;
  data: { name: string; value: number }[];
  emptyLabel: string;
}) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const showSliceLabels = useMediaQuery(theme.breakpoints.up("md"));

  const chartHeight = isXs ? 220 : isMdDown ? 270 : 300;
  const innerR = isXs ? 40 : isMdDown ? 52 : 60;
  const outerR = isXs ? 68 : isMdDown ? 86 : 100;

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: { xs: 16, sm: 18 },
          mb: { xs: 2, sm: 3 },
          wordBreak: "break-word",
        }}
      >
        {title}
      </Typography>
      {data.length > 0 ? (
        <Box
          sx={{ width: "100%", height: chartHeight, minHeight: chartHeight }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={innerR}
                outerRadius={outerR}
                paddingAngle={5}
                dataKey="value"
                labelLine={showSliceLabels}
                label={
                  showSliceLabels
                    ? ({ name, percent }) =>
                        `${name} ${((percent ?? 0) * 100).toFixed(0)}%`
                    : false
                }
              >
                {data.map((_, index) => (
                  <Cell
                    key={`cell-${title}-${index}`}
                    fill={
                      REPORT_CHART_COLORS[index % REPORT_CHART_COLORS.length]
                    }
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend
                wrapperStyle={{
                  fontSize: isXs ? 11 : 12,
                  paddingTop: isXs ? 8 : 12,
                }}
                layout="horizontal"
                verticalAlign="bottom"
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Typography
          sx={{
            color: "text.secondary",
            textAlign: "center",
            py: 6,
          }}
        >
          {emptyLabel}
        </Typography>
      )}
    </Box>
  );
}

export default function Reports() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useAppDispatch();
  const { items: entries } = useAppSelector((s) => s.entries);
  const { items: categories } = useAppSelector((s) => s.categories);

  const [reportPeriod, setReportPeriod] = useState<ReportPeriod>("month");
  const [periodOffset, setPeriodOffset] = useState(0);

  useEffect(() => {
    dispatch(fetchEntries());
    dispatch(fetchCategories());
  }, [dispatch]);

  const periodBounds = useMemo(
    () => getReportPeriodBounds(reportPeriod, periodOffset),
    [reportPeriod, periodOffset],
  );

  const entriesInPeriod = useMemo(
    () =>
      filterEntriesByYmdRange(entries, periodBounds.start, periodBounds.end),
    [entries, periodBounds.start, periodBounds.end],
  );

  const { totalExpenses, totalGains, net, entryCount } = useExpenseSummary(
    entriesInPeriod,
    categories,
  );
  const {
    categoryDataExpenses,
    categoryDataGains,
    subcategoryDataExpenses,
    subcategoryDataGains,
    trendChartData,
  } = useReportsAggregates(
    entriesInPeriod,
    categories,
    reportPeriod,
    periodBounds.start,
    periodBounds.end,
  );

  const handleReportPeriodChange = (_: unknown, next: ReportPeriod | null) => {
    if (next == null) return;
    setReportPeriod(next);
    setPeriodOffset(0);
  };

  const trendPointCount = trendChartData.length;
  const tiltTrendAxis = trendPointCount > 8;
  const trendChartHeight = isXs ? 300 : 340;
  const barMargin = isXs
    ? {
        top: 8,
        right: 4,
        left: -12,
        bottom: tiltTrendAxis ? 16 : 12,
      }
    : {
        top: 8,
        right: 16,
        left: 0,
        bottom: tiltTrendAxis ? 24 : 16,
      };

  return (
    <PageContainer>
      <PageHeader title={t("reports.title")} subtitle={t("reports.subtitle")} />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          mb: 3,
          gap: 2,
        }}
      >
        <ToggleButtonGroup
          exclusive
          value={reportPeriod}
          onChange={handleReportPeriodChange}
          size="small"
          color="secondary"
          fullWidth={isXs}
          aria-label={t("reports.periodLabel")}
          sx={{
            "& .MuiToggleButton-root": {
              flex: isXs ? 1 : undefined,
              px: { xs: 0.5, sm: 1.5 },
              fontSize: { xs: "0.75rem", sm: "0.8125rem" },
            },
          }}
        >
          <ToggleButton value="week">{t("reports.periodWeek")}</ToggleButton>
          <ToggleButton value="month">{t("reports.periodMonth")}</ToggleButton>
          <ToggleButton value="year">{t("reports.periodYear")}</ToggleButton>
        </ToggleButtonGroup>

        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: { xs: "center", sm: "flex-end" },
            flexWrap: "wrap",
            gap: 0.5,
          }}
        >
          <IconButton
            size="medium"
            onClick={() => setPeriodOffset((o) => o - 1)}
            aria-label={t("reports.periodPrevious")}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontWeight: 600,
              px: 0.5,
              textAlign: "center",
              maxWidth: { xs: "100%", sm: 360 },
              wordBreak: "break-word",
            }}
          >
            {periodBounds.label}
          </Typography>
          <IconButton
            size="medium"
            onClick={() => setPeriodOffset((o) => o + 1)}
            aria-label={t("reports.periodNext")}
          >
            <ChevronRightIcon />
          </IconButton>
          {periodOffset !== 0 ? (
            <Button
              variant="text"
              size="small"
              onClick={() => setPeriodOffset(0)}
              sx={{
                ml: { xs: 0, sm: 0.5 },
                textTransform: "none",
                minWidth: "auto",
              }}
            >
              {t("reports.periodReset")}
            </Button>
          ) : null}
        </Stack>
      </Stack>
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: "flex" }}>
          <StatCard
            icon={<TrendingDownIcon />}
            label={t("home.totalExpenses")}
            value={`${totalExpenses.toFixed(2)} EUR`}
            accentColor={STAT_ACCENTS.expenses.color}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: "flex" }}>
          <StatCard
            icon={<TrendingUpIcon />}
            label={t("home.totalGains")}
            value={`${totalGains.toFixed(2)} EUR`}
            accentColor={STAT_ACCENTS.gains.color}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: "flex" }}>
          <StatCard
            icon={<AccountBalanceWalletIcon />}
            label={t("home.netBalance")}
            value={`${net.toFixed(2)} EUR`}
            accentColor={STAT_ACCENTS.net.color}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: "flex" }}>
          <StatCard
            icon={<ReceiptIcon />}
            label={t("home.totalEntries")}
            value={String(entryCount)}
            accentColor={STAT_ACCENTS.entries.color}
          />
        </Grid>
      </Grid>
      <Grid container spacing={{ xs: 2, sm: 3, lg: 4 }}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "12px",
              p: { xs: 2, sm: 3 },
              height: "100%",
              maxWidth: "100%",
            }}
          >
            <PieBlock
              title={t("reports.spendingByCategory")}
              data={categoryDataExpenses}
              emptyLabel={t("common.noData")}
            />
          </Card>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "12px",
              p: { xs: 2, sm: 3 },
              height: "100%",
              maxWidth: "100%",
            }}
          >
            <PieBlock
              title={t("reports.gainsByCategory")}
              data={categoryDataGains}
              emptyLabel={t("common.noData")}
            />
          </Card>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "12px",
              p: { xs: 2, sm: 3 },
              height: "100%",
              maxWidth: "100%",
            }}
          >
            <PieBlock
              title={t("reports.spendingBySubcategory")}
              data={subcategoryDataExpenses}
              emptyLabel={t("common.noData")}
            />
          </Card>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "12px",
              p: { xs: 2, sm: 3 },
              height: "100%",
              maxWidth: "100%",
            }}
          >
            <PieBlock
              title={t("reports.gainsBySubcategory")}
              data={subcategoryDataGains}
              emptyLabel={t("common.noData")}
            />
          </Card>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: "12px",
              p: { xs: 2, sm: 3 },
              maxWidth: "100%",
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: { xs: 16, sm: 18 },
                mb: { xs: 2, sm: 3 },
                wordBreak: "break-word",
              }}
            >
              {t("reports.trendExpensesVsGains")}
            </Typography>
            {trendChartData.length > 0 ? (
              <Box
                sx={{
                  width: "100%",
                  height: trendChartHeight,
                  minHeight: trendChartHeight,
                  minWidth: 0,
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendChartData} margin={barMargin}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="label"
                      interval="preserveStartEnd"
                      minTickGap={isXs ? 4 : 8}
                      tick={{ fontSize: isXs ? 10 : 12 }}
                      angle={tiltTrendAxis ? -40 : 0}
                      textAnchor={tiltTrendAxis ? "end" : "middle"}
                      height={tiltTrendAxis ? (isXs ? 70 : 56) : 36}
                    />
                    <YAxis
                      width={isXs ? 36 : 48}
                      tick={{ fontSize: isXs ? 10 : 12 }}
                    />
                    <Tooltip />
                    <Legend
                      wrapperStyle={{ fontSize: isXs ? 11 : 12 }}
                      verticalAlign="top"
                    />
                    <Bar
                      dataKey="expenses"
                      name={t("tracker.tabExpenses")}
                      fill={BRAND.danger}
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="gains"
                      name={t("tracker.tabGains")}
                      fill={BRAND.green}
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Typography
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  py: 6,
                }}
              >
                {t("common.noData")}
              </Typography>
            )}
          </Card>
        </Grid>
      </Grid>
    </PageContainer>
  );
}
